import { createContext, useContext } from "react";
import { createStore, useStore } from "zustand";
import { immer } from "zustand/middleware/immer";
import { useShallow } from "zustand/shallow";

import type { SelectAudienceOption } from "@/features/BroadcastV2/components/SelectAudienceField";
import type { TextComposerPrimaryChannel } from "@/features/TextComposerV2/channel/channelDerivation";
import type { Frame } from "@/types/dragon-tiles";

import {
  DEFAULT_AB_TEST_CONFIG,
  DEFAULT_EMAIL_VARIATION_CONTENT,
  MAX_VARIATIONS,
  isTestDurationValid,
  makeEmailVariation,
  makeSmsVariation,
  smsVariationFrameName,
  variationLabel,
  type AbTestConfig,
  type EmailVariationContent,
  type TestDurationUnit,
  type VariationDraft,
  type WinningMetric,
} from "@/features/BroadcastV2/store/variationTypes";
import { DEFAULT_REACH_OPTIMIZATION_PERCENTAGE } from "@/features/BroadcastV2/types";
// Shared dayjs instance with the utc + timezone plugins already extended.
import dayjs from "@/utils/configuredDayjs";

export type SendMode = "instant" | "schedule";

/** Default send time for a plan-flow draft: 4:00 PM on the campaign's first
 * day (site timezone, past quiet hours), clamped to now once the campaign has
 * started. Keeps the field non-empty so a draft never falls back to instant. */
export function defaultScheduleWindowSendAt(
  window: { startDate: string; endDate: string },
  timezone?: string,
): string {
  const start = (
    timezone ? dayjs.tz(window.startDate, timezone) : dayjs(window.startDate)
  ).hour(16);
  const now = dayjs();
  return (start.isAfter(now) ? start : now).toISOString();
}

/** Campaign broadcasts never send directly from the composer: locked when
 * stamped calendar-owned or composing inside a plan scheduleWindow. */
export function isCampaignLocked(
  calendarOwned: boolean | undefined,
  scheduleWindow: { startDate: string; endDate: string } | null | undefined,
): boolean {
  return Boolean(calendarOwned) || Boolean(scheduleWindow);
}

export interface BroadcastComposeState {
  // Shared (email + SMS)
  name: string;
  audienceIds: string[];
  audienceOptions: SelectAudienceOption[];
  /** Audiences to exclude from the send ("do not send to"). */
  excludedAudienceIds: string[];
  excludedAudienceOptions: SelectAudienceOption[];
  sendMode: SendMode;
  /** Smart Send toggle → broadcast `smartSend`. Independent of `sendMode`. */
  smartSend: boolean;
  /** Smart Audience toggle → broadcast `reachOptimization`. */
  reachOptimization: boolean;
  /** Smart Audience trim percentage (1–99); shown only when the toggle is on. */
  reachOptimizationPercentage: number;
  enforceFrequencyCap: boolean;
  scheduledAtIso: string | null;
  confirmSend: string;
  /**
   * Campaign date span ("YYYY-MM-DD") when composing from the Marketing
   * Calendar plan wizard. Constrains scheduling: Send Instantly is disabled
   * and the schedule picker is clamped to this range. Null outside the plan
   * flow.
   */
  scheduleWindow: { startDate: string; endDate: string } | null;
  /** The loaded broadcast carries the "marketing_calendar" source stamp: lock
   * Send Instantly and the confirm-send gate even before (or without) the
   * owning campaign's scheduleWindow being resolved. */
  calendarOwned: boolean;

  // SMS-specific (stable type for follow-up PRs)
  primaryChannel: TextComposerPrimaryChannel;

  // Validation — follows AnalogGraphStore lastSaveErrors pattern.
  // Per-variation field errors are keyed by path, e.g. "variations.0.fromName".
  fieldErrors: Record<string, string>;

  // N-variant model. length >= 1; A/B is "on" when length > 1.
  variations: VariationDraft[];
  activeVariationIndex: number;
  /**
   * Extra variations parked while A/B is toggled off. Preserved in-session so an
   * accidental toggle doesn't wipe work, but never read by payload/validation —
   * so they are not sent or persisted until A/B is turned back on.
   */
  stashedVariations: VariationDraft[];

  // A/B config — only emitted when variations.length > 1.
  abTest: AbTestConfig;
}

export interface BroadcastComposeActions {
  setName: (name: string) => void;
  setAudiences: (ids: string[], options: SelectAudienceOption[]) => void;
  setExcludedAudiences: (
    ids: string[],
    options: SelectAudienceOption[],
  ) => void;
  setSendMode: (mode: SendMode) => void;
  setSmartSend: (value: boolean) => void;
  setReachOptimization: (value: boolean) => void;
  setReachOptimizationPercentage: (value: number) => void;
  setEnforceFrequencyCap: (value: boolean) => void;
  setScheduledAtIso: (value: string | null) => void;
  setConfirmSend: (value: string) => void;
  setPrimaryChannel: (channel: TextComposerPrimaryChannel) => void;
  setFieldErrors: (errors: Record<string, string>) => void;
  clearFieldError: (field: string) => void;
  reset: (initial: Partial<BroadcastComposeState>) => void;

  // Variation management (index-based; ids are stable but the UI uses indices).
  /**
   * Append a variation (up to MAX_VARIATIONS). Email content is seeded from
   * `copyFromIndex` (defaults to 0); the frame is copied only if `copyFrame`.
   * Returns the new active index, or the current count if at the cap.
   */
  addVariation: (opts?: {
    copyFromIndex?: number;
    copyFrame?: boolean;
  }) => void;
  removeVariation: (index: number) => void;
  /** A/B off: park variations[1..] in `stashedVariations`, keep only A active. */
  stashExtraVariations: () => void;
  /** A/B on: restore parked variations back onto the active list. */
  restoreStashedVariations: () => void;
  setActiveVariation: (index: number) => void;
  updateVariationEmail: (
    index: number,
    patch: Partial<EmailVariationContent>,
  ) => void;
  setVariationFrame: (index: number, frame: Frame) => void;
  clearVariationFrame: (index: number) => void;

  // A/B config
  setWinningMetric: (metric: WinningMetric) => void;
  setTestSizePercent: (percent: number) => void;
  setTestDurationValue: (value: number) => void;
  setTestDurationUnit: (unit: TestDurationUnit) => void;
}

export interface BroadcastComposeStore extends BroadcastComposeState {
  actions: BroadcastComposeActions;
}

function createDefaultState(): BroadcastComposeState {
  return {
    name: "",
    audienceIds: [],
    audienceOptions: [],
    excludedAudienceIds: [],
    excludedAudienceOptions: [],
    sendMode: "schedule",
    smartSend: false,
    reachOptimization: false,
    reachOptimizationPercentage: DEFAULT_REACH_OPTIMIZATION_PERCENTAGE,
    enforceFrequencyCap: false,
    scheduledAtIso: null,
    confirmSend: "",
    scheduleWindow: null,
    calendarOwned: false,
    primaryChannel: "SMS",
    fieldErrors: {},
    variations: [makeEmailVariation()],
    activeVariationIndex: 0,
    stashedVariations: [],
    abTest: { ...DEFAULT_AB_TEST_CONFIG },
  };
}

/** Keep email.frameId / frameId in sync after an email patch. */
function syncFrameId(variation: VariationDraft): void {
  if (variation.email) {
    variation.frameId = variation.email.frameId;
  }
}

export function createBroadcastComposeStore(
  initialState: Partial<BroadcastComposeState> = {},
) {
  return createStore<BroadcastComposeStore>()(
    immer((set) => {
      const actions: BroadcastComposeActions = {
        setName(name) {
          set((s) => {
            s.name = name;
            delete s.fieldErrors.name;
          });
        },
        setAudiences(ids, options) {
          set((s) => {
            s.audienceIds = ids;
            s.audienceOptions = options;
            delete s.fieldErrors.audienceIds;
            // An audience cannot be both a target and an exclusion (the backend
            // rejects that combination), so drop any newly-targeted audiences
            // from the exclusion list.
            const targetIds = new Set(ids);
            if (s.excludedAudienceIds.some((id) => targetIds.has(id))) {
              s.excludedAudienceOptions = s.excludedAudienceOptions.filter(
                (o) => !targetIds.has(o.id),
              );
              s.excludedAudienceIds = s.excludedAudienceOptions.map(
                (o) => o.id,
              );
            }
          });
        },
        setExcludedAudiences(ids, options) {
          set((s) => {
            s.excludedAudienceIds = ids;
            s.excludedAudienceOptions = options;
            // An audience cannot be both a target and an exclusion (the backend
            // rejects that combination), so drop any newly-excluded audiences
            // from the target list.
            const excludedIds = new Set(ids);
            if (s.audienceIds.some((id) => excludedIds.has(id))) {
              s.audienceOptions = s.audienceOptions.filter(
                (o) => !excludedIds.has(o.id),
              );
              s.audienceIds = s.audienceOptions.map((o) => o.id);
            }
          });
        },
        setSendMode(mode) {
          set((s) => {
            s.sendMode = mode;
          });
        },
        setSmartSend(value) {
          set((s) => {
            s.smartSend = value;
          });
        },
        setReachOptimization(value) {
          set((s) => {
            s.reachOptimization = value;
          });
        },
        setReachOptimizationPercentage(value) {
          set((s) => {
            // The wire value is an int64-as-string in 1–99; round fractional
            // input from the number field and ignore non-finite values so the
            // store never holds a fraction or NaN.
            if (!Number.isFinite(value)) return;
            s.reachOptimizationPercentage = Math.min(
              99,
              Math.max(1, Math.round(value)),
            );
          });
        },
        setEnforceFrequencyCap(value) {
          set((s) => {
            s.enforceFrequencyCap = value;
          });
        },
        setScheduledAtIso(value) {
          set((s) => {
            s.scheduledAtIso = value;
            delete s.fieldErrors.scheduledAtIso;
          });
        },
        setConfirmSend(value) {
          set((s) => {
            s.confirmSend = value;
            delete s.fieldErrors.confirmSend;
          });
        },
        setPrimaryChannel(channel) {
          set((s) => {
            s.primaryChannel = channel;
          });
        },
        setFieldErrors(errors) {
          set((s) => {
            s.fieldErrors = errors;
          });
        },
        clearFieldError(field) {
          set((s) => {
            delete s.fieldErrors[field];
          });
        },
        reset(initial) {
          set((s) => {
            Object.assign(s, createDefaultState(), initial);
            s.fieldErrors = {};
          });
        },

        addVariation(opts) {
          set((s) => {
            if (s.variations.length >= MAX_VARIATIONS) return;
            const fromIndex = opts?.copyFromIndex ?? 0;
            const source = s.variations[fromIndex];
            const sourceEmail = source?.email;
            // New variation's 0-based index is the current length.
            const label = variationLabel(s.variations.length);
            // Channel-agnostic: email variations carry an `email` payload; SMS
            // variations have none (their authoring lives in the draft-store
            // registry, copied separately via useAddSmsVariation).
            const newVariation = sourceEmail
              ? makeEmailVariation(
                  {
                    fromName: sourceEmail.fromName,
                    fromEmail: sourceEmail.fromEmail,
                    replyTo: sourceEmail.replyTo,
                    frameId: opts?.copyFrame ? sourceEmail.frameId : null,
                    selectedFrame: opts?.copyFrame
                      ? sourceEmail.selectedFrame
                      : null,
                  },
                  label,
                )
              : makeSmsVariation(
                  smsVariationFrameName(
                    s.name,
                    s.variations.length,
                    s.variations.length + 1,
                  ),
                  opts?.copyFrame ? (source?.frameId ?? null) : null,
                );
            s.variations.push(newVariation);
            s.activeVariationIndex = s.variations.length - 1;
          });
        },
        removeVariation(index) {
          set((s) => {
            if (s.variations.length <= 1) return;
            s.variations.splice(index, 1);
            if (s.activeVariationIndex >= s.variations.length) {
              s.activeVariationIndex = s.variations.length - 1;
            }
          });
        },
        stashExtraVariations() {
          set((s) => {
            if (s.variations.length <= 1) return;
            s.stashedVariations = s.variations.slice(1);
            s.variations = s.variations.slice(0, 1);
            s.activeVariationIndex = 0;
          });
        },
        restoreStashedVariations() {
          set((s) => {
            if (s.stashedVariations.length === 0) return;
            s.variations.push(...s.stashedVariations);
            s.stashedVariations = [];
            s.activeVariationIndex = 0;
          });
        },
        setActiveVariation(index) {
          set((s) => {
            if (index >= 0 && index < s.variations.length) {
              s.activeVariationIndex = index;
            }
          });
        },
        updateVariationEmail(index, patch) {
          set((s) => {
            const variation = s.variations[index];
            if (!variation) return;
            variation.email = {
              ...DEFAULT_EMAIL_VARIATION_CONTENT,
              ...variation.email,
              ...patch,
            };
            syncFrameId(variation);
            // Clear any per-field errors for the patched keys.
            for (const key of Object.keys(patch)) {
              delete s.fieldErrors[`variations.${index}.${key}`];
            }
          });
        },
        setVariationFrame(index, frame) {
          set((s) => {
            const variation = s.variations[index];
            if (!variation) return;
            variation.email = {
              ...DEFAULT_EMAIL_VARIATION_CONTENT,
              ...variation.email,
              frameId: frame.id,
              selectedFrame: frame,
            };
            variation.frameId = frame.id;
            delete s.fieldErrors[`variations.${index}.frameId`];
          });
        },
        clearVariationFrame(index) {
          set((s) => {
            const variation = s.variations[index];
            if (!variation) return;
            variation.email = {
              ...DEFAULT_EMAIL_VARIATION_CONTENT,
              ...variation.email,
              frameId: null,
              selectedFrame: null,
            };
            variation.frameId = null;
          });
        },

        setWinningMetric(metric) {
          set((s) => {
            s.abTest.winningMetric = metric;
          });
        },
        setTestSizePercent(percent) {
          set((s) => {
            s.abTest.testSizePercent = Math.min(100, Math.max(1, percent));
          });
        },
        setTestDurationValue(value) {
          set((s) => {
            // Store the raw value (no clamping) so the user can type freely.
            // The 4-hour minimum is enforced via inline validation + save-gating
            // (see isTestDurationValid / useIsAbDurationInvalid).
            if (Number.isFinite(value)) {
              s.abTest.testDurationValue = value;
            }
          });
        },
        setTestDurationUnit(unit) {
          set((s) => {
            // Switching units does not silently change the number; if the result
            // is under the 4-hour minimum, validation surfaces it.
            s.abTest.testDurationUnit = unit;
          });
        },
      };

      return {
        ...createDefaultState(),
        ...initialState,
        actions,
      };
    }),
  );
}

export type BroadcastComposeStoreApi = ReturnType<
  typeof createBroadcastComposeStore
>;

// --- Context ---

export const BroadcastComposeStoreContext =
  createContext<BroadcastComposeStoreApi | null>(null);

// --- Hooks ---

function useBroadcastComposeContext(): BroadcastComposeStoreApi {
  const store = useContext(BroadcastComposeStoreContext);
  if (!store) {
    throw new Error(
      "useBroadcastComposeStore must be used within a BroadcastComposeProvider",
    );
  }
  return store;
}

export function useBroadcastComposeStore<T>(
  selector: (state: BroadcastComposeStore) => T,
): T {
  const store = useBroadcastComposeContext();
  return useStore(store, selector);
}

export function useBroadcastComposeStoreShallow<T>(
  selector: (state: BroadcastComposeStore) => T,
): T {
  const store = useBroadcastComposeContext();
  return useStore(store, useShallow(selector));
}

export function useBroadcastComposeActions(): BroadcastComposeActions {
  return useBroadcastComposeStore((s) => s.actions);
}

/**
 * True when A/B is on, there is a winner send (test size < 100%), and the
 * configured test duration is under the 4-hour minimum. Used to surface inline
 * errors and gate Send/Activate + Save-as-draft.
 */
export function useIsAbDurationInvalid(): boolean {
  return useBroadcastComposeStore(
    (s) =>
      s.variations.length > 1 &&
      s.abTest.testSizePercent < 100 &&
      !isTestDurationValid(
        s.abTest.testDurationValue,
        s.abTest.testDurationUnit,
      ),
  );
}

export function useBroadcastComposeStoreApi(): BroadcastComposeStoreApi {
  return useBroadcastComposeContext();
}
